import { ActionIcon } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import classnames from 'classnames';
import { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from 'src/Components/Calendars/MonthCalendar/MonthCalendar.types';
import { EventDtoTypeEnum } from 'src/Services/API/generated';

import styles from './RoomPeriodPanel.module.scss';
import { Props } from './RoomPeriodPanel.types';

const SLEEPING_TYPES: EventDtoTypeEnum[] = [EventDtoTypeEnum.Occupation, EventDtoTypeEnum.Location];

// Mirrors the guard in `StaysTimeline` — the backend occasionally returns a non-array `rooms`.
const roomsOf = (event: CalendarEvent) => (Array.isArray(event.rooms) ? event.rooms : []);

/**
 * Panneau latéral qui résume l'état des chambres sur la période choisie dans la timeline :
 * pour chaque chambre, dit si elle est libre, prise sur une partie, ou prise par qui.
 * Les séjours REFUSED/CANCELLED ne sont jamais passés ici (le hook events les a déjà filtrés).
 */
const RoomPeriodPanel = ({ start, end, rooms, events, onClose }: Props) => {
  const { t, i18n } = useTranslation();

  const nights = Math.max(1, end.diff(start, 'day'));

  const rangeLabel = useMemo(() => {
    const sameMonth = start.isSame(end, 'month');
    const startFmt = start.locale(i18n.language).format(sameMonth ? 'D' : 'D MMMM');
    const endFmt = end.locale(i18n.language).format('D MMMM YYYY');
    return `${startFmt} → ${endFmt}`;
  }, [start, end, i18n.language]);

  const perRoom = useMemo(() => {
    return rooms.map(room => {
      const overlapping = events.filter(e => {
        if (!SLEEPING_TYPES.includes(e.type)) return false;
        if (!roomsOf(e).some(r => r.id === room.id)) return false;
        const eStart = e.startTime;
        const eEnd = e.endTime ?? e.startTime;
        return eStart.isBefore(end) && eEnd.isAfter(start);
      });

      if (overlapping.length === 0) {
        return { room, status: 'free' as const, bookings: [] };
      }

      const bookings = overlapping.map(e => ({
        id: e.localId,
        name:
          e.household?.name ??
          e.households?.[0]?.name ??
          e.name ??
          t('planning.rooms.unknownGuest', 'Séjour'),
        startTime: e.startTime,
        endTime: e.endTime ?? e.startTime,
      }));

      const fullyBooked = overlapping.some(
        e => !e.startTime.isAfter(start) && !(e.endTime ?? e.startTime).isBefore(end)
      );

      return {
        room,
        status: fullyBooked ? ('booked' as const) : ('partial' as const),
        bookings,
      };
    });
  }, [rooms, events, start, end, t]);

  const describeBooking = (b: { name: string; startTime: Dayjs; endTime: Dayjs }) =>
    t('planning.rooms.bookingLabel', {
      defaultValue: '{{name}} du {{start}} au {{end}}',
      name: b.name,
      start: b.startTime.locale(i18n.language).format('D MMM'),
      end: b.endTime.locale(i18n.language).format('D MMM'),
    });

  return (
    <div
      className={styles.container}
      role="dialog"
      aria-label={t('planning.rooms.panelTitle', 'Détail de la période')}
    >
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>{t('planning.rooms.panelTitle', 'Détail de la période')}</h3>
          <p className={styles.subtitle}>
            {rangeLabel} · {t('planning.rooms.nightsCount', { count: nights })}
          </p>
        </div>
        <ActionIcon variant="subtle" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
          <IconX size={18} />
        </ActionIcon>
      </div>

      <ul className={styles.rooms}>
        {perRoom.map(({ room, status, bookings }) => (
          <li key={room.id} className={styles.room}>
            <div>
              <span className={styles.roomName}>{room.name}</span>
              {room.buildingName && (
                <span className={styles.roomBuilding}>· {room.buildingName}</span>
              )}
            </div>
            <div
              className={classnames(styles.roomStatus, {
                [styles.statusFree]: status === 'free',
                [styles.statusPartial]: status === 'partial',
                [styles.statusBooked]: status === 'booked',
              })}
            >
              {status === 'free' && t('planning.rooms.statusFree', 'Libre')}
              {status === 'partial' && (
                <>
                  {t('planning.rooms.statusPartial', 'Libre en partie')}
                  {' · '}
                  {bookings.map(b => describeBooking(b)).join(', ')}
                </>
              )}
              {status === 'booked' && bookings.map(b => describeBooking(b)).join(', ')}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RoomPeriodPanel;
